export type Vector2 = { x: number; y: number };
export type Color = { r: number; g: number; b: number };

export abstract class FloatTypeArr {
    abstract Data: Float32Array;
}

export class Mat4 extends FloatTypeArr {
    Data: Float32Array;
    constructor() {
        super();
        this.Data = new Float32Array(16);
    }
    Set(inMat: Matrix4x4) {
        const arr = inMat.serializeForGPUImport();
        this.Data.set(arr);
    }
}

export class Float extends FloatTypeArr {
    Data: Float32Array;
    constructor(value: number) {
        super();
        this.Data = new Float32Array([value]);
    }
    get x(): number {
        return this.Data[0];
    }
    set x(value: number) {
        this.Data[0] = value;
    }
}

export class Vector3 extends FloatTypeArr {
    /* x: number;

    y: number;

    z: number; */

    Data: Float32Array;

    constructor(inX: number, inY: number, inZ: number) {
        super();
        this.Data = new Float32Array([inX, inY, inZ]);
    }

    /* Set(inX: number, inY: number, inZ: number) {
        this.x = inX;
        this.y = inY;
        this.z = inZ;
    } */

    get x(): number {
        return this.Data[0];
    }

    set x(value: number) {
        this.Data[0] = value;
    }

    get y(): number {
        return this.Data[1];
    }

    set y(value: number) {
        this.Data[1] = value;
    }

    get z(): number {
        return this.Data[2];
    }

    set z(value: number) {
        this.Data[2] = value;
    }

    Set(inVec3: Vector3) {
        this.x = inVec3.x;
        this.y = inVec3.y;
        this.z = inVec3.z;
    }

    Set3(a: number, b: number, c: number) {
        this.x = a;
        this.y = b;
        this.z = c;
    }

    SetSame(a: number) {
        this.x = a;
        this.y = a;
        this.z = a;
    }

    Mul(scale: number) {
        this.x *= scale;
        this.y *= scale;
        this.z *= scale;
    }

    Mul3(a: number, b: number, c: number) {
        this.x *= a;
        this.y *= b;
        this.z *= c;
    }

    Negate(inVec: Vector3) {
        this.x -= inVec.x;
        this.y -= inVec.y;
        this.z -= inVec.z;
    }

    Add(inVec: Vector3) {
        this.x += inVec.x;
        this.y += inVec.y;
        this.z += inVec.z;
    }

    Cross(b: Vector3) {
        const x = this.y * b.z - this.z * b.y;
        const y = this.z * b.x - this.x * b.z;
        const z = this.x * b.y - this.y * b.x;
        this.x = x;
        this.y = y;
        this.z = z;
    }

    Dot(b: Vector3) {
        return this.x * b.x + this.y * b.y + this.z * b.z;
    }

    Normalize() {
        const length = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
        if (length === 0) {
            // Avoid division by zero
        } else {
            this.x = this.x / length;
            this.y = this.y / length;
            this.z = this.z / length;
        }
    }
}

export function GetVec2(r: number, g: number): Vector2 {
    return { x: r, y: g };
}

export function SetVec2(vec: Vector2, x: number, y: number) {
    vec.x = x;
    vec.y = y;
}

export function GetVec3(r: number, g: number, b: number): Vector3 {
    return new Vector3(r, g, b);
}

export function ConvertHexColorToVec3(hexColor: string, inoutVec3: Vector3) {
    const hex = hexColor.replace(/^#/, "");
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    inoutVec3.Set3(r / 255, g / 255, b / 255);
}

export class Matrix4x4 {
    elements: number[][]; //rc
    elementsSerialized: number[] = [];

    constructor() {
        this.elements = [
            [1, 0, 0, 0],
            [0, 1, 0, 0],
            [0, 0, 1, 0],
            [0, 0, 0, 1],
        ];
    }

    // Multiply this matrix by another matrix
    multiply(matrix: Matrix4x4): Matrix4x4 {
        const result = new Matrix4x4();
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                let sum = 0;
                for (let k = 0; k < 4; k++) {
                    sum += this.elements[i][k] * matrix.elements[k][j];
                }
                result.elements[i][j] = sum;
            }
        }
        return result;
    }

    setBasisVectors(basisX: Vector3, basisY: Vector3, basisZ: Vector3) {
        this.elements[0][0] = basisX.x;
        this.elements[0][1] = basisX.y;
        this.elements[0][2] = basisX.z;
        this.elements[1][0] = basisY.x;
        this.elements[1][1] = basisY.y;
        this.elements[1][2] = basisY.z;
        this.elements[2][0] = basisZ.x;
        this.elements[2][1] = basisZ.y;
        this.elements[2][2] = basisZ.z;
    }

    setTranslation(translation: Vector3) {
        this.elements[3][0] = translation.x;
        this.elements[3][1] = translation.y;
        this.elements[3][2] = translation.z;
    }

    getDeterminant(): number {
        const a = this.elements[0][0];
        const b = this.elements[0][1];
        const c = this.elements[0][2];
        const d = this.elements[0][3];
        const e = this.elements[1][0];
        const f = this.elements[1][1];
        const g = this.elements[1][2];
        const h = this.elements[1][3];
        const i = this.elements[2][0];
        const j = this.elements[2][1];
        const k = this.elements[2][2];
        const l = this.elements[2][3];
        const m = this.elements[3][0];
        const n = this.elements[3][1];
        const o = this.elements[3][2];
        const p = this.elements[3][3];

        const det =
            a * f * k * p -
            a * f * l * o -
            a * g * j * p +
            a * g * l * n +
            a * h * j * o -
            a * h * k * n -
            b * e * k * p +
            b * e * l * o +
            b * g * i * p -
            b * g * l * m -
            b * h * i * o +
            b * h * k * m +
            c * e * j * p -
            c * e * l * n -
            c * f * i * p +
            c * f * l * m +
            c * h * i * n -
            c * h * j * m -
            d * e * j * o +
            d * e * k * n +
            d * f * i * o -
            d * f * k * m -
            d * g * i * n +
            d * g * j * m;

        return det;
    }

    serializeForGPUImport(): number[] {
        let c = 0;
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                this.elementsSerialized[c] = this.elements[j][i]; // Transpose and pack into column-major order
                c++;
            }
        }
        return this.elementsSerialized;
    }

    static ConstructLocalToWorldMatrix(pos: Vector3, yawPitchRoll: Vector3, scale: Vector3, outMat: Matrix4x4): void {
        // Translation
        outMat.elements[3][0] = pos.x;
        outMat.elements[3][1] = pos.y;
        outMat.elements[3][2] = pos.z;

        // Scale
        outMat.elements[0][0] *= scale.x;
        outMat.elements[1][1] *= scale.y;
        outMat.elements[2][2] *= scale.z;

        // Orientation (yaw, pitch, roll)
        const cy = Math.cos(yawPitchRoll.y);
        const sy = Math.sin(yawPitchRoll.y);
        const cp = Math.cos(yawPitchRoll.x);
        const sp = Math.sin(yawPitchRoll.x);
        const cr = Math.cos(yawPitchRoll.z);
        const sr = Math.sin(yawPitchRoll.z);

        outMat.elements[0][0] = cy * cr * scale.x;
        outMat.elements[1][0] = (cy * sr * sp - sy * cp) * scale.y;
        outMat.elements[2][0] = (cy * sr * cp + sy * sp) * scale.z;

        outMat.elements[0][1] = sy * cr * scale.x;
        outMat.elements[1][1] = (sy * sr * sp + cy * cp) * scale.y;
        outMat.elements[2][1] = (sy * sr * cp - cy * sp) * scale.z;

        outMat.elements[0][2] = -sr * scale.x;
        outMat.elements[1][2] = cr * sp * scale.y;
        outMat.elements[2][2] = cr * cp * scale.z;

        outMat.elements[0][3] = 0;
        outMat.elements[1][3] = 0;
        outMat.elements[2][3] = 0;
        outMat.elements[3][3] = 1;
    }

    static ConstructRotationMatrix(yawPitchRoll: Vector3, outMat: Matrix4x4): void {
        // Orientation (yaw, pitch, roll)
        const cy = Math.cos(yawPitchRoll.y);
        const sy = Math.sin(yawPitchRoll.y);
        const cp = Math.cos(yawPitchRoll.x);
        const sp = Math.sin(yawPitchRoll.x);
        const cr = Math.cos(yawPitchRoll.z);
        const sr = Math.sin(yawPitchRoll.z);

        outMat.elements[0][0] = cy * cr;
        outMat.elements[1][0] = cy * sr * sp - sy * cp;
        outMat.elements[2][0] = cy * sr * cp + sy * sp;

        outMat.elements[0][1] = sy * cr;
        outMat.elements[1][1] = sy * sr * sp + cy * cp;
        outMat.elements[2][1] = sy * sr * cp - cy * sp;

        outMat.elements[0][2] = -sr;
        outMat.elements[1][2] = cr * sp;
        outMat.elements[2][2] = cr * cp;

        outMat.elements[0][3] = 0;
        outMat.elements[1][3] = 0;
        outMat.elements[2][3] = 0;
        outMat.elements[3][3] = 1;
    }
}

//To avoid heap allocations
export class GStack {
    static readonly TempVec3 = new Vector3(0, 0, 0);

    static readonly TempVec3_2 = new Vector3(0, 0, 0);

    static readonly TempVec3_3 = new Vector3(0, 0, 0);

    static readonly TempVec3_4 = new Vector3(0, 0, 0);
    static readonly TempVec3_5 = new Vector3(0, 0, 0);
    static readonly TempVec3_6 = new Vector3(0, 0, 0);
    static readonly TempVec3_7 = new Vector3(0, 0, 0);
}
